import * as React from "react";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useFileInputCancelListener } from "../../hooks/useFileInputCancelListener";
import { useDispatch, useSelector } from 'react-redux';
import { addGlossaryFile, removeGlossaryFile } from '../../store/chatSlice';
import { IGlossarySpec } from '../../utils/documentTranslation.service';
import { RootState } from '../../store/store';
import { PlusCircledIcon, CheckCircledIcon, Cross2Icon } from "@radix-ui/react-icons";
import { useTheme } from "../Theme/ThemeProvider";
import { utf8ToBase64 } from "../../utils/base64.utils";

export type TranslationWarningType = { title: string; subtitle?: string; message: string };

export interface ILanguageOption {
  label: string;
  languageCode: string;
}

export type { TranslationWarningType as TranslationWarning };

export const languageList: ILanguageOption[] = [
  {"label":"Afrikaans","languageCode":"af"},
  {"label":"Albanian","languageCode":"sq"},
  {"label":"Arabic","languageCode":"ar"},
  {"label":"Azerbaijani (Latin)","languageCode":"az"},
  {"label":"Bashkir","languageCode":"ba"},
  {"label":"Basque","languageCode":"eu"},
  {"label":"Bosnian (Latin)","languageCode":"bs"},
  {"label":"Bulgarian","languageCode":"bg"},
  {"label":"Cantonese (Traditional)","languageCode":"yue"},
  {"label":"Catalan","languageCode":"ca"},
  {"label":"Chinese (Literary)","languageCode":"lzh"},
  {"label":"Chinese Simplified","languageCode":"zh-Hans"},
  {"label":"Chinese Traditional","languageCode":"zh-Hant"},
  {"label":"Croatian","languageCode":"hr"},
  {"label":"Czech","languageCode":"cs"},
  {"label":"Danish","languageCode":"da"},
  {"label":"Dutch","languageCode":"nl"},
  {"label":"English","languageCode":"en"},
  {"label":"Estonian","languageCode":"et"},
  {"label":"Faroese","languageCode":"fo"},
  {"label":"Fijian","languageCode":"fj"},
  {"label":"Filipino","languageCode":"fil"},
  {"label":"Finnish","languageCode":"fi"},
  {"label":"French","languageCode":"fr"},
  {"label":"French (Canada)","languageCode":"fr-CA"},
  {"label":"Galician","languageCode":"gl"},
  {"label":"German","languageCode":"de"},
  {"label":"Haitian Creole","languageCode":"ht"},
  {"label":"Hindi","languageCode":"hi"},
  {"label":"Hmong Daw (Latin)","languageCode":"mww"},
  {"label":"Hungarian","languageCode":"hu"},
  {"label":"Icelandic","languageCode":"is"},
  {"label":"Indonesian","languageCode":"id"},
  {"label":"Interlingua","languageCode":"ia"},
  {"label":"Inuinnaqtun","languageCode":"ikt"},
  {"label":"Inuktitut (Latin)","languageCode":"iu-Latn"},
  {"label":"Irish","languageCode":"ga"},
  {"label":"Italian","languageCode":"it"},
  {"label":"Japanese","languageCode":"ja"},
  {"label":"Kannada","languageCode":"kn"},
  {"label":"Kazakh (Cyrillic)","languageCode":"kk-Cyrl"},
  {"label":"Kazakh (Latin)","languageCode":"kk-Latn"},
  {"label":"Korean","languageCode":"ko"},
  {"label":"Kurdish (Latin)","languageCode":"ku-Latn"},
  {"label":"Kyrgyz (Cyrillic)","languageCode":"ky"},
  {"label":"Latvian","languageCode":"lv"},
  {"label":"Lithuanian","languageCode":"lt"},
  {"label":"Macedonian","languageCode":"mk"},
  {"label":"Malagasy","languageCode":"mg"},
  {"label":"Malay (Latin)","languageCode":"ms"},
  {"label":"Malayalam","languageCode":"ml"},
  {"label":"Maltese","languageCode":"mt"},
  {"label":"Maori","languageCode":"mi"},
  {"label":"Marathi","languageCode":"mr"},
  {"label":"Mongolian (Cyrillic)","languageCode":"mn-Cyrl"},
  {"label":"Nepali","languageCode":"ne"},
  {"label":"Norwegian Bokmål","languageCode":"nb"},
  {"label":"Polish","languageCode":"pl"},
  {"label":"Portuguese (Brazil)","languageCode":"pt-BR"},
  {"label":"Portuguese (Portugal)","languageCode":"pt-PT"},
  {"label":"Punjabi","languageCode":"pa"},
  {"label":"Queretaro Otomi","languageCode":"otq"},
  {"label":"Romanian","languageCode":"ro"},
  {"label":"Russian","languageCode":"ru"},
  {"label":"Samoan (Latin)","languageCode":"sm"},
  {"label":"Serbian (Cyrillic)","languageCode":"sr-Cyrl"},
  {"label":"Serbian (Latin)","languageCode":"sr-Latn"},
  {"label":"Slovak","languageCode":"sk"},
  {"label":"Slovenian","languageCode":"sl"},
  {"label":"Somali (Arabic)","languageCode":"so"},
  {"label":"Spanish","languageCode":"es"},
  {"label":"Swahili (Latin)","languageCode":"sw"},
  {"label":"Swedish","languageCode":"sv"},
  {"label":"Tahitian","languageCode":"ty"},
  {"label":"Tamil","languageCode":"ta"},
  {"label":"Tatar (Latin)","languageCode":"tt"},
  {"label":"Telugu","languageCode":"te"},
  {"label":"Tongan","languageCode":"to"},
  {"label":"Turkish","languageCode":"tr"},
  {"label":"Turkmen (Latin)","languageCode":"tk"},
  {"label":"Ukrainian","languageCode":"uk"},
  {"label":"Upper Sorbian","languageCode":"hsb"},
  {"label":"Uzbek (Latin)","languageCode":"uz"},
  {"label":"Vietnamese","languageCode":"vi"},
  {"label":"Welsh","languageCode":"cy"},
  {"label":"Yucatec Maya","languageCode":"yua"},
  {"label":"Zulu","languageCode":"zu"}
];

// Lightweight language picker (no react-select)
interface LanguagePickerProps {
  value: ILanguageOption[];
  onChange: (selected: ILanguageOption[]) => void;
  onSelectionLimitExceeded?: (message: string) => void;
  onGlossaryFileTooLarge?: (title: string, message: string) => void;
  onMultipleGlossaryFiles?: (warning: { title: string; subtitle?: string; message: string }) => void;
  maxSelections?: number;
  chatPage?: boolean;
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  value,
  onChange,
  onSelectionLimitExceeded,
  onGlossaryFileTooLarge,
  onMultipleGlossaryFiles,
  maxSelections = 10,
}) => {
  const [search, setSearch] = React.useState('');
  const { isMobile } = useDeviceDetection();
  const { mode } = useTheme();
  const dispatch = useDispatch();
  const glossaryFiles = useSelector((state: RootState) => state.chat.glossaryFiles || [] as IGlossarySpec[]);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const pendingLanguageRef = React.useRef<string | null>(null);
  const activeReadersRef = React.useRef<Map<string, FileReader>>(new Map());
  // Per-upload cancel tokens: setting token.cancelled = true before the rAF runs is guaranteed
  // to be seen by the rAF closure regardless of when/whether onabort fires.
  const activeCancelTokensRef = React.useRef<Map<string, { cancelled: boolean }>>(new Map());
  const [hoveredGlossaryLang, setHoveredGlossaryLang] = React.useState<string | null>(null);
  const [loadingGlossaryLangs, setLoadingGlossaryLangs] = React.useState<Set<string>>(new Set());
  const [hoveredLoadingLang, setHoveredLoadingLang] = React.useState<string | null>(null);

  const selectedCodes = React.useMemo(() => {
    return new Set(value.map(v => v.languageCode));
  }, [value]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const filteredAll = term
      ? languageList.filter(l => l.label.toLowerCase().includes(term) || l.languageCode.toLowerCase().includes(term))
      : languageList.slice();
    // Put selected at top
    const selected: ILanguageOption[] = [];
    const unselected: ILanguageOption[] = [];
    filteredAll.forEach(l => {
      if (selectedCodes.has(l.languageCode)) selected.push(l);
      else unselected.push(l);
    });
    return [...selected, ...unselected];
  }, [search, selectedCodes]);

  const langHasGlossary = React.useCallback((languageCode: string) => {
    return Array.isArray(glossaryFiles) && glossaryFiles.some((g: IGlossarySpec) => g.lang === languageCode);
  }, [glossaryFiles]);

  const getGlossaryFileForLang = React.useCallback((languageCode: string) => {
    return Array.isArray(glossaryFiles) ? glossaryFiles.find((g: IGlossarySpec) => g.lang === languageCode) : null;
  }, [glossaryFiles]);

  const clearLangLoading = React.useCallback((languageCode: string) => {
    activeReadersRef.current.delete(languageCode);
    activeCancelTokensRef.current.delete(languageCode);
    setLoadingGlossaryLangs(prev => {
      const next = new Set(prev);
      next.delete(languageCode);
      return next;
    });
  }, []);

  const clearPendingGlossaryLoading = React.useCallback(() => {
    const languageCode = pendingLanguageRef.current;
    if (!languageCode) {
      return;
    }

    pendingLanguageRef.current = null;
    clearLangLoading(languageCode);
  }, [clearLangLoading]);

  const handleRemoveGlossary = (languageCode: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const glossaryFile = getGlossaryFileForLang(languageCode);
    if (glossaryFile) {
      dispatch(removeGlossaryFile({ languageCode, name: glossaryFile.file.filename }));
    }
  };


  const toggle = (opt: ILanguageOption) => {
    const exists = selectedCodes.has(opt.languageCode);
    if (exists) {
      onChange(value.filter(v => v.languageCode !== opt.languageCode));
      setSearch('');
    } else if (value.length >= maxSelections) {
        onSelectionLimitExceeded && onSelectionLimitExceeded(`You can select up to ${maxSelections} target languages.`);
        return;
    } else {
      onChange([...value, opt]);
      setSearch('');
    };
  };

  const handleGlossaryClick = (languageCode: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Show spinner immediately — before the file dialog so any processing delay is covered
    setLoadingGlossaryLangs(prev => new Set(prev).add(languageCode));
    pendingLanguageRef.current = languageCode;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  useFileInputCancelListener(fileInputRef, clearPendingGlossaryLoading);
  React.useEffect(() => {
    if (hoveredLoadingLang && !loadingGlossaryLangs.has(hoveredLoadingLang)) {
      setHoveredGlossaryLang(hoveredLoadingLang);
      setHoveredLoadingLang(null);
    }
  }, [loadingGlossaryLangs, hoveredLoadingLang]);

  // Helper function to convert CSV to TSV
  const convertCSVtoTSV = (csvContent: string): string => {
    const lines = csvContent.split('\n');
    const tsvLines = lines.map(line => {
      const fields: string[] = [];
      const chars: string[] = [];
      let insideQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (insideQuotes && line[i + 1] === '"') {
            chars.push('"');
            i++;
          } else {
            insideQuotes = !insideQuotes;
          }
        } else if (char === ',' && !insideQuotes) {
          let field = chars.join('');
          if (field.endsWith('\r')) {
            field = field.slice(0, -1);
          }
          fields.push(field);
          chars.length = 0;
        } else {
          chars.push(char);
        }
      }
      let lastField = chars.join('');
      if (lastField.endsWith('\r')) {
        lastField = lastField.slice(0, -1);
      }
      fields.push(lastField);
      return fields.join('\t');
    });
    return tsvLines.join('\n');
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    // Capture and clear pendingLanguageRef up-front
    const lang = pendingLanguageRef.current;
    pendingLanguageRef.current = null;

    const clearLangLoading = (languageCode: string) => {
      activeReadersRef.current.delete(languageCode);
      setLoadingGlossaryLangs(prev => { const next = new Set(prev); next.delete(languageCode); return next; });
    };

    // If user cancelled the file dialog (no files / no lang), clear the spinner
    if (!lang) { e.currentTarget.value = ''; return; }
    if (!files || files.length === 0) { clearLangLoading(lang); e.currentTarget.value = ''; return; }
    
    // Check if multiple glossary files were selected
    if (files.length > 1) {
      const keptFile = files[0].name;
      const discardedFiles = Array.from(files).slice(1).map(f => f.name);
      
      if (onMultipleGlossaryFiles) {
        onMultipleGlossaryFiles({
          title: 'Only One Glossary File Allowed',
          subtitle: 'Upload a single glossary file per language.',
          message: `Added:\n- ${keptFile}\n\nDiscarded:\n${discardedFiles.map(f => `- ${f}`).join('\n')}`
        });
      }
    }
    
    const GLOSSARY_MAX_BYTES = 10 * 1024 * 1024; // 10MB max per glossary file
    
    // Process only the first file (ignore the rest)
    const file = files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isCSV = fileName.endsWith('.csv');
      const isTSV = fileName.endsWith('.tsv');
      
      // Check file type — clear spinner so the user can try again
      if (!isCSV && !isTSV) {
        console.warn('Only .csv and .tsv glossary files are accepted:', file.name);
        clearLangLoading(lang);
        return;
      }
      
      // Check file size — clear spinner then show error
      if (file.size > GLOSSARY_MAX_BYTES) {
        console.warn(`Glossary file exceeds 10MB limit: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        clearLangLoading(lang);
        if (onGlossaryFileTooLarge) {
          onGlossaryFileTooLarge(
            'File Too Large',
            `"${file.name}" exceeds the 10MB glossary file size limit. Please upload a smaller file.`
          );
        }
        return;
      }
      
      const reader = new FileReader();
      // Loading state already set in handleGlossaryClick — store reader and a per-upload cancel token
      activeReadersRef.current.set(lang, reader);
      const cancelToken = { cancelled: false };
      activeCancelTokensRef.current.set(lang, cancelToken);

      const finishLoading = () => clearLangLoading(lang);

      reader.onload = () => {
        const text = String(reader.result || '');
        // Defer heavy sync work (CSV conversion + base64) to the next animation frame
        // so the spinner stays visible during processing.
        requestAnimationFrame(() => {
          // cancelToken.cancelled is set synchronously in the click handler, so it is always
          // visible here regardless of whether/when onabort fires.
          if (cancelToken.cancelled) {
            finishLoading();
            return;
          }
          let contentToEncode = text;
          let finalFilename = file.name;
          if (isCSV) {
            contentToEncode = convertCSVtoTSV(text);
            finalFilename = file.name.replace(/\.csv$/i, '.tsv');
          }
          // Convert UTF-8 text to base64 using shared helper
          const contentBase64 = utf8ToBase64(contentToEncode);
          const spec: IGlossarySpec = {
            lang,
            format: 'TSV', // Always store as TSV for API consistency
            file: {
              filename: finalFilename,
              content_base64: contentBase64,
            }
          };
          // Dispatch first so state goes spinner → checkmark in one transition
          dispatch(addGlossaryFile(spec));
          finishLoading();
        });
      };
      reader.onabort = finishLoading;
      reader.onerror = finishLoading;
      reader.readAsText(file);
    }
    e.currentTarget.value = '';
  };

  return (
  <div className="w-full flex flex-col">
      <div style={{ marginBottom: 8 }}>
        <input
          aria-label="Search languages"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder=" Search"
          className="language-search-input text-sm text-[var(--color-text)] w-full border border-[#5E95ED] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5E95ED]/50 bg-[var(--color-background)]"
        />
      </div>

      {/* Options list */}
        <div
          className={`border border-[#5E95ED] rounded-md ${isMobile ? 'h-[400px]' : 'h-[260px]'} overflow-y-auto scrollbar-hide`}
          style={{ position: 'relative' }}
        >
          {(() => {
            const selected = filtered.filter(opt => selectedCodes.has(opt.languageCode));
            const unselected = filtered.filter(opt => !selectedCodes.has(opt.languageCode));
            return (
              <>
                {/* Sticky selected options at the top */}
                {selected.length > 0 && (
                  <div
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: 'var(--color-background)',
                      width: '100%',
                      // Use a pseudo-element for a solid overlay
                      // fallback for browsers: add a white background for light mode, dark for dark mode
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                        pointerEvents: 'none',
                        background: mode === 'dark' ? '#18181b' : '#fff',
                        opacity: 1,
                      }}
                    />
                    <div style={{ position: 'relative', zIndex: 2 }}>
                    {selected.map((opt) => (
                      // Use a div instead of <label> so the browser never synthesises a
                      // label-activation click on the checkbox when the glossary button is pressed.
                      <div
                        key={opt.languageCode}
                        className={`flex py-2 px-3 text-sm text-[var(--color-text)] gap-3 items-center cursor-pointer hover:font-bold`}
                        style={{ background: 'transparent' }}
                        onClick={() => { toggle(opt); }}
                      >
                        <input
                          id={`language-selector-${opt.languageCode}`}
                          type="checkbox"
                          checked={true}
                          onChange={() => toggle(opt)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[var(--color-text)] rounded-lg focus:ring-0 p-5 cursor-pointer items-center"
                          style={{ width: 16, height: 16, accentColor: '#5E95ED' }}
                        />
                        <div className="flex justify-between items-center flex-1">
                          <span className="text-[var(--color-text)] transition-all" style={{ position: 'relative', top: '1px' }}>{opt.label}</span>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              // ALL logic is here instead of onClick because onMouseEnter state
                              // changes cause a re-render between mousedown and mouseup, which
                              // makes the browser drop the click event entirely.
                              e.preventDefault();
                              e.stopPropagation();
                              if (loadingGlossaryLangs.has(opt.languageCode)) {
                                const token = activeCancelTokensRef.current.get(opt.languageCode);
                                if (token) token.cancelled = true;
                                const reader = activeReadersRef.current.get(opt.languageCode);
                                if (reader) {
                                  reader.abort();
                                } else {
                                  pendingLanguageRef.current = null;
                                  activeCancelTokensRef.current.delete(opt.languageCode);
                                  activeReadersRef.current.delete(opt.languageCode);
                                  setLoadingGlossaryLangs(prev => { const next = new Set(prev); next.delete(opt.languageCode); return next; });
                                }
                              } else if (langHasGlossary(opt.languageCode)) {
                                handleRemoveGlossary(opt.languageCode, e);
                              } else {
                                handleGlossaryClick(opt.languageCode, e);
                              }
                            }}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onMouseEnter={() => {
                              if (loadingGlossaryLangs.has(opt.languageCode)) {
                                setHoveredLoadingLang(opt.languageCode);
                              } else if (langHasGlossary(opt.languageCode)) {
                                setHoveredGlossaryLang(opt.languageCode);
                              }
                            }}
                            onMouseLeave={() => { setHoveredGlossaryLang(null); setHoveredLoadingLang(null); }}
                            title={
                              loadingGlossaryLangs.has(opt.languageCode)
                                ? (hoveredLoadingLang === opt.languageCode ? 'Cancel upload' : 'Uploading glossary…')
                                : langHasGlossary(opt.languageCode)
                                  ? (hoveredGlossaryLang === opt.languageCode ? 'Remove glossary' : 'Glossary uploaded. Hover to remove')
                                  : 'Upload glossary (.tsv or .csv) for this language'
                            }
                            aria-label={
                              loadingGlossaryLangs.has(opt.languageCode)
                                ? `Uploading glossary for ${opt.label}`
                                : langHasGlossary(opt.languageCode)
                                  ? `Glossary uploaded for ${opt.label}`
                                  : `Upload glossary for ${opt.label}`
                            }
                            className={`flex items-center justify-center transition-all ${
                              loadingGlossaryLangs.has(opt.languageCode)
                                ? 'hover:bg-red-500/10'
                                : langHasGlossary(opt.languageCode)
                                  ? hoveredGlossaryLang === opt.languageCode
                                    ? 'hover:bg-[#5E95ED]/10'
                                    : 'hover:bg-[#10b981]/10'
                                  : 'hover:bg-[#5E95ED]/10'
                            }`}
                            style={{ width: '28px', minWidth: '28px', height: '28px', borderRadius: '50%', padding: 0, alignSelf: 'center', flexShrink: 0,
                              // Always allow clicks on this button even when the parent
                              // wrapper has pointer-events:none (e.g. during file upload)
                              pointerEvents: 'auto' }}
                          >
                            {loadingGlossaryLangs.has(opt.languageCode) ? (
                              hoveredLoadingLang === opt.languageCode ? (
                                <Cross2Icon width={16} height={16} color="#f87171"/>
                              ) : (
                                <svg className="animate-spin" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth={2.5} strokeLinecap="round">
                                  <circle cx="12" cy="12" r="10" stroke="#5E95ED" strokeOpacity={0.25} strokeWidth={2.5} fill="none"/>
                                  <path d="M12 2 a10 10 0 0 1 10 10" stroke="#5E95ED" strokeWidth={2.5} strokeLinecap="round" fill="none"/>
                                </svg>
                              )
                            ) : langHasGlossary(opt.languageCode) ? (
                              hoveredGlossaryLang === opt.languageCode ? (
                                <Cross2Icon width={16} height={16} color="#5E95ED"/>
                              ) : (
                                <CheckCircledIcon width={20} height={20} color="#10b981"/>
                              )
                            ) : (
                              <PlusCircledIcon width={20} height={20} color="#5E95ED"/>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Border only if there are selected and unselected languages */}
                    {selected.length > 0 && unselected.length > 0 && (
                      <div style={{ borderBottom: '2px solid #5E95ED', margin: 0 }} />
                    )}
                    </div>
                  </div>
                )}
                {/* Unselected options scroll underneath */}
                {unselected.map((opt) => (
                  <label
                    key={opt.languageCode}
                    className={`flex p-3 text-sm text-[var(--color-text)] gap-3 items-normal cursor-pointer ${mode === 'dark' ? 'hover:bg-[#202024]' : 'hover:bg-[#202024]/10'}`}
                  >
                    <input
                      id={`language-selector-${opt.languageCode}`}
                      type="checkbox"
                      checked={false}
                      onChange={() => toggle(opt)}
                      className="text-[var(--color-text)] rounded-lg focus:ring-0 p-5 cursor-pointer items-center"
                      style={{ width: 16, height: 16, accentColor: '#5E95ED' }}
                    />
                    <div className="flex justify-between flex-1">
                      <span className="text-[var(--color-text)]">{opt.label}</span>
                    </div>
                  </label>
                ))}
              </>
            );
          })()}
        </div>
        {/* hidden file input for glossary uploads (.tsv and .csv only) */}
        <input ref={fileInputRef} type="file" accept=".tsv,.csv" multiple style={{ display: 'none' }} onChange={onFileInputChange} onAbort={onFileInputChange}/>
    </div>
  );
};